import { type IMenuItemNavProps } from 'shared/ui/Menu/ui/MenuItem/MenuItemNav/MenuItemNav'
import { Status, Typography } from 'shared/ui'
import { type IMenuItemDefaultProps } from 'shared/ui/Menu/ui/MenuItem/MenuItemDefault/MenuItemDefault'
import { type EnumUserRoles } from 'entities/Users'
import { checkAccessPage, EnumAccessPageKeys } from 'entities/ExportRouters/lib/checkAccessPage'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type MediaLibraryStore } from 'widgets/MediaLibrary'
import { ComplianceRoutes } from 'pages/settings/pages/compliance'
import { KnowledgeBaseRoutes } from 'pages/settings/pages/knowledgeBase'
import { IntegrationsRoutes } from 'pages/settings/pages/integrations/route/type'
import { BillingRoutes } from 'pages/settings/pages/billing/route/type'
import { ReferralsRoutes } from 'pages/settings/pages/referrals'
import { EmailToSmsRoutes } from 'pages/settings/pages/emailToSms'
import { InboxesRoutes } from 'pages/settings/pages/inboxes'
import { MembersRoutes } from 'pages/settings/pages/members'
import { CustomFieldsRoutes } from 'pages/settings/pages/customFields'
import { RcsRoutes } from 'pages/settings/pages/rcs/route/type'
import { SavedRepliesRoutes } from 'pages/settings/pages/savedReplies/route/type'
import { PersonalPath } from 'pages/settings/pages/personal/route/personalPath'
import { DeveloperPath } from 'pages/settings/pages/developer/route/developerPath'
import { NotificationsRoutes } from 'pages/settings/pages/notifications/route/type'
import { SettingsRoutes } from '../route/type'
import styles from './styles.module.scss'

type GetWorkspaceAllItemsParams = {
  canManageMembers: boolean
  canManageBillingSettings: boolean
  canAddSubscription: boolean
  canManageIntegrations: boolean
  canManageTeams: boolean
}

export const getWorkspaceAllItems = (
  {
    canManageMembers,
    canManageBillingSettings,
    canAddSubscription,
    canManageIntegrations,
    canManageTeams,
  }: GetWorkspaceAllItemsParams,
  entities: IEntitiesStore
): IMenuItemNavProps[] => {
  const canMenageCompliance = checkAccessPage(EnumAccessPageKeys.compliance_settings, entities)
  const canManageAddOns = checkAccessPage(EnumAccessPageKeys.add_ons, entities)
  const canManageRolesPermissions = checkAccessPage(EnumAccessPageKeys.roles_permissions, entities)
  const canManageSettingsOrganization = checkAccessPage(
    EnumAccessPageKeys.settings_organization,
    entities
  )

  const items: IMenuItemNavProps[] = []
  if (canManageSettingsOrganization) {
    items.push({
      key: 'organization',
      type: 'nav',
      to: SettingsRoutes.general,
      label: 'Organization',
      icon: 'building2',
    })
  }

  if (canManageMembers) {
    items.push({
      key: 'members',
      type: 'nav',
      to: MembersRoutes.root,
      label: 'Members',
      icon: 'users',
    })
  }

  if (canManageRolesPermissions) {
    items.push({
      key: 'roles_permissions',
      type: 'nav',
      to: SettingsRoutes.rolesPermissions,
      label: 'Role & Permissions',
      icon: 'unlock',
    })
  }

  items.push({
    key: 'inboxes',
    type: 'nav',
    to: InboxesRoutes.root,
    label: 'Inboxes',
    icon: 'inboxes2',
  })

  items.push({
    key: 'numbers',
    type: 'nav',
    to: SettingsRoutes.numbers,
    label: 'Numbers',
    icon: 'phone',
  })

  if (canManageAddOns) {
    items.push({
      key: 'add_ons',
      type: 'nav',
      to: SettingsRoutes.addOns,
      label: 'Add-ons',
      icon: 'addOn',
    })
  }

  if (canManageTeams) {
    items.push({
      key: 'teams',
      type: 'nav',
      to: SettingsRoutes.teams,
      label: 'Teams',
      icon: 'user2',
    })
  }

  if (canManageIntegrations) {
    items.push({
      key: 'app_marketplace',
      type: 'nav',
      to: IntegrationsRoutes.root,
      label: 'App Marketplace',
      icon: 'plug',
    })
  }

  if (canMenageCompliance) {
    items.push({
      key: 'compliance',
      type: 'nav',
      to: ComplianceRoutes.root,
      label: 'Compliance',
      icon: 'shield',
    })
  }

  if (canManageBillingSettings) {
    items.push({
      key: 'plan_billing',
      type: 'nav',
      to: SettingsRoutes.planAndBilling,
      label: 'Plan & Billing',
      icon: 'card',
    })
  }

  if (canAddSubscription) {
    const isBillingUpgradeV2 = entities.featureFlagsStore.billing_upgrade_v2

    items.push({
      key: 'add_subscription',
      type: 'nav',
      to: isBillingUpgradeV2
        ? `${SettingsRoutes.root}/${BillingRoutes.upgrade}`
        : SettingsRoutes.addSubscription,
      onClick: () => {
        if (!isBillingUpgradeV2) {
          window.publicApiVue?.showEarlyUpgrade()
        }
      },
      label: 'Add subscription',
      icon: 'card',
    })
  }

  return items
}

export const getProductAllItems = (
  mediaLibraryStore: MediaLibraryStore,
  entities: IEntitiesStore
): (IMenuItemDefaultProps | IMenuItemNavProps)[] => {
  const items: (IMenuItemDefaultProps | IMenuItemNavProps)[] = [
    {
      key: 'tags',
      type: 'nav',
      to: SettingsRoutes.tags,
      label: 'Tags',
      icon: 'tag',
    },
    {
      key: 'custom_fields',
      type: 'nav',
      to: CustomFieldsRoutes.root,
      label: 'Custom Fields',
      icon: 'textInput2',
    },
    {
      key: 'url_shortener',
      type: 'nav',
      to: SettingsRoutes.urlShortener,
      label: 'URL Shortener',
      icon: 'link',
    },
    {
      key: 'website_chat_widget',
      type: 'nav',
      to: SettingsRoutes.websiteChatWidget,
      label: 'Website Chat Widget',
      icon: 'messageTextCircle02',
    },
    {
      key: 'email_to_sms',
      type: 'nav',
      to: `${SettingsRoutes.organization}/${EmailToSmsRoutes.root}`,
      label: 'Email to SMS',
      icon: 'email',
    },
    {
      key: 'ai_knowledge_base',
      type: 'nav',
      to: KnowledgeBaseRoutes.root,
      label: (
        <Typography
          variant={'body-md-regular'}
          className={styles.linkWithBadge}
          ariaLabel={'AI knowledge base'}
        >
          AI knowledge base
        </Typography>
      ),
      icon: 'bulb',
      rightContent: <Status title={'Beta'} intent={'warning'} emphasis={'high'} size={'label'} />,
    },
    {
      key: 'quick_links',
      type: 'nav',
      to: SettingsRoutes.quickLinks,
      label: (
        <Typography
          className={styles.linkWithBadge}
          variant={'body-md-regular'}
          ariaLabel={'Quick links'}
        >
          Quick links
        </Typography>
      ),
      icon: 'linkExternal',
      rightContent: <Status title={'New'} intent={'purple'} emphasis={'high'} size={'label'} />,
    },
  ]

  if (!entities.usersStore.user?.isViewOnlyRole) {
    items.push({
      key: 'media_library',
      type: 'default',
      label: 'Media library',
      variant: 'nav',
      onClick: mediaLibraryStore.handleOpenMediaLibraryModal,
      icon: 'folder2',
    })
  }

  if (entities.featureFlagsStore.canManageRCS) {
    items.push({
      key: 'rcs',
      type: 'nav',
      to: RcsRoutes.root,
      label: (
        <Typography className={styles.linkWithBadge} variant={'body-md-regular'} ariaLabel={'RCS'}>
          RCS
        </Typography>
      ),
      icon: 'rcs',
      rightContent: <Status title={'Beta'} intent={'warning'} emphasis={'high'} size={'label'} />,
    })
  }

  if (entities.featureFlagsStore.savedRepliesV2) {
    items.splice(1, 0, {
      key: 'saved_replies',
      type: 'nav',
      to: SavedRepliesRoutes.root,
      label: 'Saved Replies',
      icon: 'savedReplies',
    })
  }

  return items
}

export const getPersonalAllItems = (): IMenuItemNavProps[] => {
  return [
    {
      key: 'account',
      type: 'nav',
      to: PersonalPath.personalFull,
      label: 'Account',
      icon: 'userFilled',
    },
    {
      key: 'notifications',
      type: 'nav',
      to: NotificationsRoutes.root,
      label: 'Notifications',
      icon: 'bellFilled',
    },
    {
      key: 'referrals',
      type: 'nav',
      to: ReferralsRoutes.referralsFull,
      label: 'Referrals',
      icon: 'dollar',
      rightContent: <Status title={'New'} intent={'purple'} emphasis={'high'} size={'label'} />,
    },
  ]
}

export const getDeveloperItems = (entities: IEntitiesStore): IMenuItemNavProps[] => {
  const canManageAuthorizedApps = checkAccessPage(EnumAccessPageKeys.authorized_apps, entities)
  const canManageAccessTokens = checkAccessPage(EnumAccessPageKeys.access_tokens, entities)
  const canManageApplications = checkAccessPage(EnumAccessPageKeys.applications, entities)

  const items: IMenuItemNavProps[] = []

  if (canManageApplications) {
    items.push({
      key: 'OAuth_applications',
      type: 'nav',
      to: DeveloperPath.applications,
      label: 'OAuth Applications',
      icon: 'container',
    })
  }

  if (canManageAccessTokens) {
    items.push({
      key: 'personal_access_tokens',
      type: 'nav',
      to: DeveloperPath.accessTokens,
      label: 'Personal Access Tokens',
      icon: 'container',
    })
  }

  if (canManageAuthorizedApps) {
    items.push({
      key: 'authorized_apps',
      type: 'nav',
      to: PersonalPath.authorizedApps,
      label: 'Authorized Apps',
      icon: 'container',
    })
  }

  return items
}

export const workspaceRolesMap: Record<`${EnumUserRoles}`, string[]> = {
  view_only: [IntegrationsRoutes.root],
  member: [
    SettingsRoutes.inboxes,
    InboxesRoutes.root,
    SettingsRoutes.numbers,
    IntegrationsRoutes.root,
    SettingsRoutes.members,
    MembersRoutes.root,
  ],
  manager: [
    SettingsRoutes.general,
    SettingsRoutes.inboxes,
    InboxesRoutes.root,
    SettingsRoutes.numbers,
    IntegrationsRoutes.root,
    SettingsRoutes.members,
    MembersRoutes.root,
  ],
  admin: [],
  owner: [],
}

export const canceledWorkspaceRolesMap: Record<`${EnumUserRoles}`, string[]> = {
  view_only: [IntegrationsRoutes.root],
  member: [
    SettingsRoutes.inboxes,
    InboxesRoutes.root,
    SettingsRoutes.numbers,
    IntegrationsRoutes.root,
  ],
  manager: [
    SettingsRoutes.general,
    SettingsRoutes.inboxes,
    InboxesRoutes.root,
    SettingsRoutes.numbers,
    IntegrationsRoutes.root,
  ],
  admin: [
    SettingsRoutes.general,
    ComplianceRoutes.root,
    SettingsRoutes.addSubscription,
    `${SettingsRoutes.root}/${BillingRoutes.upgrade}`,
    SettingsRoutes.addOns,
  ],
  owner: [
    SettingsRoutes.general,
    ComplianceRoutes.root,
    SettingsRoutes.addSubscription,
    `${SettingsRoutes.root}/${BillingRoutes.upgrade}`,
    SettingsRoutes.inboxes,
    InboxesRoutes.root,
    SettingsRoutes.numbers,
    SettingsRoutes.members,
    SettingsRoutes.rolesPermissions,
    MembersRoutes.root,
    SettingsRoutes.addOns,
  ],
}

export const productRolesMap: Record<`${EnumUserRoles}`, string[]> = {
  view_only: [SettingsRoutes.tags],
  member: [
    SettingsRoutes.tags,
    SettingsRoutes.customFields,
    CustomFieldsRoutes.root,
    SettingsRoutes.quickLinks,
    SavedRepliesRoutes.root,
  ],
  manager: [
    SettingsRoutes.tags,
    SettingsRoutes.customFields,
    CustomFieldsRoutes.root,
    SettingsRoutes.quickLinks,
    SavedRepliesRoutes.root,
  ],
  admin: [],
  owner: [],
}

export const canceledProductRolesMap: Record<`${EnumUserRoles}`, string[]> = {
  view_only: [SettingsRoutes.tags],
  member: [
    SettingsRoutes.tags,
    SettingsRoutes.customFields,
    CustomFieldsRoutes.root,
    SettingsRoutes.quickLinks,
  ],
  manager: [
    SettingsRoutes.tags,
    SettingsRoutes.customFields,
    CustomFieldsRoutes.root,
    SettingsRoutes.quickLinks,
  ],
  admin: [SettingsRoutes.urlShortener, SettingsRoutes.websiteChatWidget, SettingsRoutes.quickLinks],
  owner: [SettingsRoutes.urlShortener, SettingsRoutes.websiteChatWidget, SettingsRoutes.quickLinks],
}

export const personalRolesMap: Record<`${EnumUserRoles}`, string[]> = {
  view_only: [PersonalPath.personalFull, ReferralsRoutes.referralsFull, NotificationsRoutes.root],
  member: [PersonalPath.personalFull, ReferralsRoutes.referralsFull, NotificationsRoutes.root],
  manager: [PersonalPath.personalFull, ReferralsRoutes.referralsFull, NotificationsRoutes.root],
  admin: [],
  owner: [],
}

export const canceledPersonalRolesMap: Record<`${EnumUserRoles}`, string[]> = {
  view_only: [PersonalPath.personalFull, ReferralsRoutes.referralsFull, NotificationsRoutes.root],
  member: [PersonalPath.personalFull, ReferralsRoutes.referralsFull, NotificationsRoutes.root],
  manager: [PersonalPath.personalFull, ReferralsRoutes.referralsFull, NotificationsRoutes.root],
  admin: [PersonalPath.personalFull, ReferralsRoutes.referralsFull, NotificationsRoutes.root],
  owner: [PersonalPath.personalFull, ReferralsRoutes.referralsFull, NotificationsRoutes.root],
}

export const developerRolesMap: Record<`${EnumUserRoles}`, string[]> = {
  view_only: [],
  member: [],
  manager: [],
  admin: [],
  owner: [],
}

export const canceledDeveloperRolesMap: Record<`${EnumUserRoles}`, string[]> = {
  view_only: [DeveloperPath.applications, DeveloperPath.accessTokens],
  member: [DeveloperPath.applications, DeveloperPath.accessTokens],
  manager: [DeveloperPath.applications, DeveloperPath.accessTokens],
  admin: [DeveloperPath.applications, DeveloperPath.accessTokens],
  owner: [DeveloperPath.applications, DeveloperPath.accessTokens],
}

const reachItems = (items: (IMenuItemNavProps | IMenuItemDefaultProps)[]) =>
  items.map((item) => {
    if (item.type === 'default') return item

    if (/^(settings)\//.test(item.to)) {
      return {
        ...item,
        to: `/${item.to}`,
        checkActive: (pathname: string) => item.to && pathname.includes(item.to),
      }
    }

    return {
      ...item,
      to: `/${SettingsRoutes.root}/${item.to}`,
      checkActive: (pathname: string) => item.to && pathname.includes(item.to),
    }
  })

export const getMenuItemsForRole = (
  role: EnumUserRoles,
  items: (IMenuItemNavProps | IMenuItemDefaultProps)[],
  roleMapping: Record<EnumUserRoles, string[]>
): (IMenuItemNavProps | IMenuItemDefaultProps)[] => {
  const allowedRoutes = roleMapping[role]

  const filteredItems = items.filter((item) => {
    return item.type === 'default' || (item.to && allowedRoutes.includes(item.to))
  })

  if (allowedRoutes.length === 0) return reachItems(items)

  return reachItems(filteredItems)
}
